import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import GenericSelect from '../../../components/ui/GenericSelect';
import { Kpi } from '../../performance-operations/components/KpiCards';
import {
  fetchMetrics, formatNumber, hasValue, metaOfMetrics, metricOf, usePanel,
} from '../../performance-operations/config';
import { KpiSelect } from '../styles';
import { SUMMARY_CODES, defaultM03Stage } from '../config';

// Spec §7.2 — 8 KPI (4 + 4): M01, M02, M03 (kartta aşama seçimi; varsayılan katalog sırasında %100 olmayan ilk zorunlu aşama),
// M04 (görev + kişi), M05 (alt satırda geçiş öncesi görevler hariç: N), M48, M49, M50. M03 "kişi bazlı", görev sayıları "görev bazlı"
// diye etiketlenir (analiz §5). M03 değeri aşama × durum matrisiyle AYNI aşama kırılımı isteğinden okunur (§19/6).
export const stageLabel = (t, key) => t(`performance.insight.stage.${key}`, { defaultValue: key });

export default function SummaryKpis({ filters, stagePanel, m03Stage, onM03StageChange, onMeta }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const key = JSON.stringify(filters);
  const metrics = usePanel(() => fetchMetrics(filters, filters.periodId, SUMMARY_CODES), [key], !!filters.periodId);
  useEffect(() => { if (metrics.data) onMeta?.('kpi', metaOfMetrics(metrics.data)); }, [metrics.data, onMeta]);

  const m = (code) => metricOf(metrics.data, code);
  const ratio = (metric) => (hasValue(metric) && metric.numerator != null && metric.denominator != null
    ? t('performance.operations.kpi.ratio', { num: formatNumber(metric.numerator, lang, 0), den: formatNumber(metric.denominator, lang, 0) })
    : null);
  const common = { loading: metrics.loading, error: metrics.error, onRetry: metrics.retry, span: 3, kpi: false };

  const stageCells = (stagePanel.data?.cells || []).filter((c) => c.key);
  const selectedStage = m03Stage && stageCells.some((c) => c.key === m03Stage) ? m03Stage : defaultM03Stage(stageCells);
  const m03 = metricOf(stageCells.find((c) => c.key === selectedStage)?.metrics, 'M03');
  const m04 = m('M04');
  const historyExcluded = m('M05')?.exclusions?.historyIncomplete || 0;
  const lines = (...items) => {
    const shown = items.filter(Boolean);
    return shown.length ? shown.map((x) => <div key={x}>{x}</div>) : null;
  };

  return (
    <>
      <Kpi code="M01" title={t('performance.operations.kpi.coverage')} metric={m('M01')} sub={t('performance.operations.kpi.people')} {...common} />
      <Kpi code="M02" title={t('performance.operations.kpi.completion')} metric={m('M02')} sub={ratio(m('M02'))} {...common} />
      <Kpi
        code="M03"
        title={t('performance.processHealth.kpi.stageCompletion')}
        metric={m03}
        sub={lines(t('performance.processHealth.kpi.personBased'), ratio(m03))}
        loading={stagePanel.loading}
        error={stagePanel.error}
        onRetry={stagePanel.retry}
        span={3}
        kpi={false}
        control={stageCells.length > 0 ? (
          <KpiSelect data-control="m03-stage">
            <GenericSelect
              size="small"
              value={selectedStage}
              onChange={(v) => { if (v) onM03StageChange(v); }}
              options={stageCells.map((c) => ({ id: c.key, name: stageLabel(t, c.key) }))}
              idKey="id"
              labelKey="name"
              clearable={false}
            />
          </KpiSelect>
        ) : null}
      />
      <Kpi
        code="M04"
        title={t('performance.operations.kpi.overdue')}
        metric={m04}
        sub={lines(
          t('performance.processHealth.kpi.taskBased'),
          hasValue(m04) && m04.numerator != null ? t('performance.processHealth.kpi.affectedPeople', { count: formatNumber(m04.numerator, lang, 0) }) : null,
        )}
        {...common}
      />
      <Kpi
        code="M05"
        title={t('performance.processHealth.kpi.onTime')}
        metric={m('M05')}
        sub={lines(
          ratio(m('M05')),
          historyExcluded > 0 ? t('performance.processHealth.kpi.historyExcluded', { count: formatNumber(historyExcluded, lang, 0) }) : null,
        )}
        {...common}
      />
      <Kpi code="M48" title={t('performance.processHealth.kpi.nomination')} metric={m('M48')} sub={ratio(m('M48'))} {...common} />
      <Kpi code="M49" title={t('performance.processHealth.kpi.published')} metric={m('M49')} sub={ratio(m('M49'))} {...common} />
      <Kpi
        code="M50"
        title={t('performance.processHealth.kpi.reopened')}
        metric={m('M50')}
        sub={lines(t('performance.processHealth.kpi.taskBased'), ratio(m('M50')))}
        {...common}
      />
    </>
  );
}
